import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart,
  Bot,
  CheckCircle2,
  Database,
  FileText,
  Globe,
  GraduationCap,
  Layers,
  Loader2,
  MessageSquare,
  Paperclip,
  Search,
  Send,
  Server,
  Sparkles,
  Target,
  TrendingUp,
  User,
  Users,
  X,
} from 'lucide-react';

const portfolioData = [
  {
    id: 'kleinanzeigen',
    company: 'Kleinanzeigen',
    role: 'Senior Product Manager',
    domain: ['Marketplace', 'B2B SaaS', 'AI Products'],
    scale: '4B+ Queries/Mo',
    metrics: [
      { label: 'YoY Revenue Growth', value: '300%' },
      { label: 'Annual Revenue Rebuild', value: '€4M+' },
      { label: 'Interim Reports', value: '24' },
    ],
    context: [
      'Led product strategy and execution for a new self serve platform from discovery to MVP in 3 months, increasing engagement and scalability.',
      'Drove 300 percent YoY revenue growth through rapid experimentation and data driven iteration.',
      'Owned the rebuild of a core platform serving 4 billion monthly queries and generating €4M+ in annual revenue, migrating to a scalable architecture supporting >50 percent annual revenue growth.',
      'Aligned 3 cross functional teams [24 people] as interim lead PM, accelerating delivery by one quarter.',
      'Integrated AI powered customer research to enhance discovery and inform product roadmap decisions.',
    ],
    mechanisms: ['AI Customer Research', 'LLM Integration', 'Rapid Experimentation', 'Architecture Migration'],
    tech: ['SQL', 'Dovetail', 'Firebase', 'OpenAI API'],
  },
  {
    id: '3ym',
    company: '3YOURMIND',
    role: 'VP Product',
    domain: ['B2B SaaS'],
    scale: 'Enterprise ERP',
    metrics: [
      { label: 'Annual Usage Growth', value: '200%' },
      { label: 'BI Product ROI', value: '215%' },
      { label: 'Teams Led', value: '4' },
    ],
    context: [
      'Defined product vision and multi year roadmap, aligning leadership and engineering for scalable growth.',
      'Built and led 4 product teams, achieving 200 percent annual usage growth and doubling revenue.',
      'Implemented OKRs and hypothesis driven development to instill a Product Led Growth culture.',
      'Launched a BI product line with 215 percent ROI in 6 months, co developed with 13 enterprise clients.',
      'Introduced agile best practices that reduced release cycles and improved cross team delivery velocity.',
    ],
    mechanisms: ['OKRs', 'Hypothesis Driven Dev', 'Cross Team Velocity', 'Client Co Creation'],
    tech: ['Looker', 'Metabase', 'Jira', 'Productboard'],
  },
  {
    id: 'careship',
    company: 'Careship',
    role: 'Product Manager',
    domain: ['Marketplace'],
    scale: 'National',
    metrics: [
      { label: 'Growth Target Exceeded', value: '150%' },
      { label: 'Delivery Time', value: '5 Mo' },
    ],
    context: [
      'Led discovery to launch of the company online booking flow in 5 months.',
      'Exceeded growth targets by 150 percent via A/B testing and marketing product collaboration.',
      'Partnered with design, BI, and CRM to improve UX and retention through user research insights.',
    ],
    mechanisms: ['A/B Testing', 'User Path Analysis', 'UX Retention'],
    tech: ['Google Optimize', 'Hotjar', 'Mixpanel'],
  },
  {
    id: 'lesara',
    company: 'Lesara',
    role: 'App Product Manager',
    domain: ['B2C E-commerce'],
    scale: '2M+ Downloads',
    metrics: [
      { label: 'Conversion Increase', value: '71%' },
      { label: 'App Revenue Share', value: '2x' },
      { label: 'App Rating', value: '4.5+' },
    ],
    context: [
      'Built and launched native iOS and Android apps in 6 months, hiring and leading a team of 12 designers and developers.',
      'Increased conversion by 71 percent, doubled app revenue share, and reached 2M+ downloads with top tier ratings.',
    ],
    mechanisms: ['Mobile Optimization', 'Rapid Validation', 'Team Building'],
    tech: ['Figma', 'Proto.io', 'AppsFlyer'],
  },
  {
    id: 'zalando',
    company: 'Zalando',
    role: 'Business Analyst Manager',
    domain: ['B2C E-commerce', 'Data & Analytics'],
    scale: '€340M+ Budget',
    metrics: [
      { label: 'Marketing Optimization', value: '€800K' },
      { label: 'Managed Budget', value: '>€340M' },
    ],
    context: [
      'Managed category planning, budgeting, and forecasting for >€340M, optimizing €800K in marketing resources through user path analysis.',
      'Delivered data insights that improved brand positioning and conversion performance.',
    ],
    mechanisms: ['Cohort Analysis', 'Forecasting', 'Budgeting'],
    tech: ['Excel', 'SQL', 'Quicksight'],
  },
  {
    id: 'prachtprint',
    company: 'Prachtprint',
    role: 'Founder',
    domain: ['B2C E-commerce', 'Entrepreneurship'],
    scale: 'Top Etsy Ranking',
    metrics: [{ label: 'Go To Market', value: '0 to 1' }],
    context: [
      'Founded and grew an online store for custom glass prints, reaching top Etsy category rankings.',
      'Gained hands on experience in go to market strategy, marketing, and operations, informing later product work.',
    ],
    mechanisms: ['Operations', 'Go To Market Strategy', 'Marketing'],
    tech: ['Etsy', 'Affinity Photo', 'Mailchimp'],
  },
];

const domainsList = ['All', 'Marketplace', 'B2B SaaS', 'B2C E-commerce', 'AI Products', 'Data & Analytics', 'Entrepreneurship'];

const notionSiteContext = `
Additional Structural Data [Notion Archive]:
Waldo focuses on high leverage product development, prioritizing asymmetric returns on engineering investment.
He treats absence of data as data and challenges baseline assumptions to locate epistemic limits.
Operational temperatures during high intensity sprints are kept regulated to standard European metrics [21 degrees Celsius average office environment].
`;

const baseSystemPrompt = `You are an AI proxy representing Waldo Vanderhaeghen, a Senior Product Leader.
Your objective is to answer recruiter queries accurately based ONLY on the provided JSON dataset of his career and the attached Notion context.
Do not invent or hallucinate information.
If a query falls outside the provided data, state that clearly and suggest contacting Waldo directly on LinkedIn.
Keep responses highly analytical, concise, and focused on leverage, scale, and revenue impact.
Do not use em dashes or en dashes in your output; use commas or brackets instead.

CV DATA: ${JSON.stringify(portfolioData)}
NOTION ARCHIVE: ${notionSiteContext}

Additional context: Waldo holds dual Masters degrees in Economics and International Politics [cum laude].
He speaks Dutch, English, German, and French.`;

export default function App() {
  const [activeDomain, setActiveDomain] = useState('All');
  const [expandedId, setExpandedId] = useState(portfolioData[0].id);

  // Chat State
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: 'System initialized. Indexing 10+ years of product leadership data, 4B+ query scale metrics, and cross domain expertise. Input query variables.',
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const chatEndRef = useRef(null);

  // Job Fit Analyzer State
  const [isAnalyzerOpen, setIsAnalyzerOpen] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisMeta, setAnalysisMeta] = useState(null); // NEW
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const filteredData = useMemo(() => {
    if (activeDomain === 'All') return portfolioData;
    return portfolioData.filter((item) => item.domain.includes(activeDomain));
  }, [activeDomain]);

  const aggregatedMetrics = useMemo(() => {
    return [
      { label: 'Roles Analyzed', value: filteredData.length, icon: Layers },
      { label: 'Primary Focus', value: activeDomain === 'All' ? 'Cross Domain' : activeDomain, icon: Search },
      { label: 'Total Track Record', value: '10+ Yrs', icon: TrendingUp },
    ];
  }, [filteredData, activeDomain]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isChatOpen) scrollToBottom();
  }, [messages, isChatOpen, isTyping]);

  // Convert File to Base64
  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = file.type === 'application/pdf' || file.type === 'text/plain';
    if (!allowed) {
      alert('Only PDF or TXT supported.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File limit exceeded. Keep attachments under 10MB.');
      return;
    }

    setUploadedFile(file);
  };

  // Extract all text parts from Gemini response (avoid truncating to parts[0]) [web:662]
  function extractGeminiText(respJson) {
    const parts = respJson?.candidates?.[0]?.content?.parts || [];
    return parts.map((p) => p?.text).filter(Boolean).join('\n');
  }

  // NEW: Extract finishReason + usage metadata so you can see why output stops (MAX_TOKENS, SAFETY, etc.) [web:662]
  function extractGeminiMeta(respJson) {
    return {
      finishReason: respJson?.candidates?.[0]?.finishReason || null,
      usageMetadata: respJson?.usageMetadata || null,
      modelVersion: respJson?.modelVersion || null,
    };
  }

  // Core Gemini API Execution (via Netlify Function)
  const executeGemini = async ({ contents, systemInstruction, mode, generationConfig }) => {
    const payload = { contents, systemInstruction, mode, generationConfig };
    const delays = [1000, 2000, 4000, 8000, 16000];
    const retries = 4;

    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch('/.netlify/functions/gemini', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const msg = data?.error?.message || data?.message || `Function error, HTTP ${response.status}`;
          throw new Error(msg);
        }

        return {
          text: extractGeminiText(data) || 'Data parsing error.',
          meta: extractGeminiMeta(data),
          raw: data,
        };
      } catch (err) {
        if (i === retries - 1) return { text: `AI call failed: ${err?.message || 'Unknown error'}`, meta: null, raw: null };
        await new Promise((res) => setTimeout(res, delays[i]));
      }
    }
  };

  // Feature 1: Chat Interface
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isTyping) return;

    const userQuery = inputValue.trim();
    setMessages((prev) => [...prev, { sender: 'user', text: userQuery }]);
    setInputValue('');
    setIsTyping(true);

    const geminiContents = [];
    messages.forEach((msg) => {
      if (msg.sender === 'bot' && msg.text.startsWith('System initialized')) return;
      geminiContents.push({
        role: msg.sender === 'bot' ? 'model' : 'user',
        parts: [{ text: msg.text }],
      });
    });

    geminiContents.push({ role: 'user', parts: [{ text: userQuery }] });

    if (geminiContents.length > 0 && geminiContents[0].role === 'model') {
      geminiContents.shift();
    }

    const botResponse = await executeGemini({
      contents: geminiContents,
      systemInstruction: baseSystemPrompt,
      mode: 'text',
      generationConfig: { temperature: 0.4, maxOutputTokens: 5000, topP: 0.9 },
    });

    setMessages((prev) => [...prev, { sender: 'bot', text: botResponse?.text ?? String(botResponse) }]);
    setIsTyping(false);
  };

  // Feature 2: Job Fit Analyzer [Multimodal Injection]
  const analyzeJobFit = async () => {
    if (!jobDescription.trim() && !uploadedFile) return;

    setIsAnalyzing(true);
    setAnalysisResult(null);
    setAnalysisMeta(null);

    const analyzerPrompt = `
You are an expert technical recruiter and AI assistant evaluating a candidate against a Job Description or Company Background document.

Analyze the provided requirements [text input or attached document] against Waldo Vanderhaeghen's CV data.

Output your analysis in plain text using the following exact structure [do NOT use markdown like asterisks or hash symbols, just use simple text formatting, spacing, and standard bullets like "-"]:

MATCH SCORE: [Provide a percentage out of 100 based on realistic fit]

TOP 3 OVERLAPPING STRENGTHS:
- [Strength 1 with specific metric/example from his CV]
- [Strength 2 with specific metric/example from his CV]
- [Strength 3 with specific metric/example from his CV]

STRUCTURAL CONSTRAINTS / RISKS:
[Identify a structural mismatch or latent assumption in the JD that Waldo's background might challenge or not explicitly cover. Surface second order implications]

RECOMMENDED INTERVIEW QUESTION:
[One highly specific, challenging question a hiring manager should ask him based on this JD to test his epistemic limits]

Text Input to Analyze:
${jobDescription || 'No text provided, see attached document.'}
`.trim();

    const parts = [{ text: analyzerPrompt }];
    const wantsMultimodal = Boolean(uploadedFile);

    if (uploadedFile) {
      try {
        const base64Data = await readFileAsBase64(uploadedFile);
        parts.push({
          inlineData: { mimeType: uploadedFile.type, data: base64Data },
        });
      } catch (error) {
        setAnalysisResult('System constraint: File encoding failed. Proceeding with text analysis only.');
      }
    }

    const response = await executeGemini({
      contents: [{ role: 'user', parts }],
      systemInstruction: baseSystemPrompt,
      mode: wantsMultimodal ? 'multimodal' : 'text',
      // Increased further, so we can verify if finishReason becomes MAX_TOKENS [web:662]
      generationConfig: { temperature: 0.2, maxOutputTokens: 4096, topP: 0.9 },
    });

    setAnalysisResult(response?.text ?? String(response));
    setAnalysisMeta(response?.meta || null);
    setIsAnalyzing(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 p-4 md:p-8 font-mono text-sm relative">
      <div className="max-w-6xl mx-auto space-y-6 pb-20">
        <header className="border-b border-gray-300 pb-6 mb-8 flex flex-col md:flex-row md:justify-between md:items-end gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2 uppercase">Waldo Vanderhaeghen</h1>
            <div className="flex flex-wrap gap-4 text-gray-600">
              <span className="flex items-center gap-1">
                <Server size={14} /> Senior Product Leader
              </span>
              <span className="flex items-center gap-1">
                <Database size={14} /> Data Driven
              </span>
              <span className="flex items-center gap-1">
                <Users size={14} /> Cross Functional Lead
              </span>
            </div>
          </div>

          <div className="flex flex-col md:items-end gap-3">
            <div className="flex flex-col gap-1 text-left md:text-right text-xs text-gray-500">
              <span className="flex items-center md:justify-end gap-1">
                <Globe size={12} /> Dutch Native, English Native, German Excellent, French Fluent
              </span>
              <span className="flex items-center md:justify-end gap-1">
                <GraduationCap size={12} /> Dual M.S. Economics Intl Politics cum laude
              </span>
            </div>

            <button
              onClick={() => setIsAnalyzerOpen(true)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-sm shadow hover:shadow-lg transition-all text-xs font-bold uppercase tracking-wide hover:-translate-y-0.5"
            >
              <Target size={14} />
              Analyze Job Fit
            </button>
          </div>
        </header>

        <div className="bg-white p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="font-semibold mb-3 text-xs uppercase tracking-wider text-gray-500">Domain Query Filter</p>
            <div className="flex flex-wrap gap-2">
              {domainsList.map((domain) => (
                <button
                  key={domain}
                  onClick={() => setActiveDomain(domain)}
                  className={`px-4 py-2 border transition-colors ${
                    activeDomain === domain
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                  }`}
                >
                  {domain}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {aggregatedMetrics.map((metric, idx) => (
            <div key={idx} className="bg-white p-4 border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs uppercase mb-1">{metric.label}</p>
                <p className="text-xl font-bold">{metric.value}</p>
              </div>
              <metric.icon className="text-gray-400" size={24} />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {filteredData.map((node) => {
            const isExpanded = expandedId === node.id;
            return (
              <div
                key={node.id}
                className={`bg-white border transition-all ${
                  isExpanded ? 'border-black shadow-md' : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div
                  className="p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                  onClick={() => setExpandedId(node.id)}
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {node.domain.map((d) => (
                        <span key={d} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold">
                          {d}
                        </span>
                      ))}
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold">{node.scale}</span>
                    </div>
                    <h2 className="text-lg font-bold uppercase">{node.company}</h2>
                    <p className="text-gray-600">{node.role}</p>
                  </div>

                  <div className="flex gap-4 md:gap-8 flex-wrap">
                    {node.metrics.map((m, i) => (
                      <div key={i} className="text-left md:text-right">
                        <p className="text-2xl font-bold text-black">{m.value}</p>
                        <p className="text-xs text-gray-500 uppercase">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 md:px-6 pb-6 pt-2 border-t border-gray-100 bg-gray-50">
                    <div className="my-4 space-y-2">
                      {node.context.map((paragraph, index) => (
                        <p key={index} className="text-gray-800 leading-relaxed flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-gray-400 mt-1 shrink-0" />
                          <span>{paragraph}</span>
                        </p>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-2">
                          <ArrowRight size={14} /> Execution Mechanisms
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {node.mechanisms.map((mech, i) => (
                            <span key={i} className="px-3 py-1 bg-white border border-gray-300 text-xs">
                              {mech}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-2">
                          <BarChart size={14} /> Technical Instrumentation
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {node.tech.map((t, i) => (
                            <span key={i} className="px-3 py-1 bg-gray-200 text-gray-800 text-xs">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isAnalyzerOpen && (
          <div className="fixed inset-0 bg-gray-900/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white border border-gray-300 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col rounded-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <Target size={18} />
                  <h3 className="font-bold text-sm tracking-wide">AI Job Fit Analyzer</h3>
                </div>
                <button onClick={() => setIsAnalyzerOpen(false)} className="text-white/80 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto bg-gray-50">
                <p className="text-sm text-gray-600 mb-4">
                  Provide a Job Description via text or document upload. The AI will evaluate structural fit and surface latent constraints.
                </p>

                <div className="flex flex-col gap-3 mb-4">
                  <textarea
                    className="w-full h-32 p-3 text-sm border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none rounded-sm resize-none shadow-inner bg-white"
                    placeholder="Paste Job Description text here..."
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                  />

                  <div className="flex items-center justify-between border border-dashed border-gray-400 bg-white p-3 rounded-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Paperclip size={16} />
                      <span className="text-xs uppercase tracking-wide">Attach Document [PDF/TXT]</span>
                    </div>
                    <div>
                      <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 text-xs border border-gray-300 transition-colors">
                        Browse Files
                        <input type="file" accept=".pdf,.txt" className="hidden" onChange={handleFileChange} />
                      </label>
                    </div>
                  </div>

                  {uploadedFile && (
                    <p className="text-xs text-indigo-600 font-bold flex items-center gap-1">
                      <CheckCircle2 size={12} />
                      {uploadedFile.name} Attached
                    </p>
                  )}
                </div>

                <button
                  onClick={analyzeJobFit}
                  disabled={(!jobDescription.trim() && !uploadedFile) || isAnalyzing}
                  className="w-full bg-black text-white py-3 px-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-md"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Parsing Payload...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Compute Match Score
                    </>
                  )}
                </button>

                {analysisResult && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText size={16} className="text-indigo-600" />
                      Analysis Report
                    </h4>
                    <div className="bg-white border border-indigo-100 p-5 rounded-sm shadow-sm whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
                      {analysisResult}
                    </div>

                    {analysisMeta && (
                      <div className="mt-3 text-xs text-gray-500 whitespace-pre-wrap">
                        finishReason: {analysisMeta.finishReason || 'unknown'}
                        {'\n'}
                        modelVersion: {analysisMeta.modelVersion || 'unknown'}
                        {'\n'}
                        usageMetadata: {analysisMeta.usageMetadata ? JSON.stringify(analysisMeta.usageMetadata, null, 2) : 'n/a'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
          {isChatOpen && (
            <div className="bg-white border border-gray-300 shadow-2xl mb-4 w-[350px] sm:w-[450px] h-[550px] flex flex-col rounded-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
              <div className="bg-black text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bot size={18} />
                  <h3 className="font-bold text-sm tracking-wide">Recruiter Copilot</h3>
                </div>
                <button onClick={() => setIsChatOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.sender === 'bot' && (
                      <div className="w-8 h-8 bg-gray-200 rounded-sm flex items-center justify-center shrink-0">
                        <Bot size={16} className="text-gray-600" />
                      </div>
                    )}

                    <div
                      className={`p-3 max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap ${
                        msg.sender === 'user'
                          ? 'bg-blue-600 text-white rounded-l-md rounded-br-md shadow-sm'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-r-md rounded-bl-md shadow-sm'
                      }`}
                    >
                      {msg.text}
                    </div>

                    {msg.sender === 'user' && (
                      <div className="w-8 h-8 bg-blue-100 rounded-sm flex items-center justify-center shrink-0">
                        <User size={16} className="text-blue-600" />
                      </div>
                    )}
                  </div>
                ))}

                {isTyping && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 bg-gray-200 rounded-sm flex items-center justify-center shrink-0">
                      <Bot size={16} className="text-gray-600" />
                    </div>
                    <div className="p-3 bg-white border border-gray-200 text-gray-800 rounded-r-md rounded-bl-md shadow-sm flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin text-gray-500" />
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Querying Matrix</span>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              <form onSubmit={handleSendMessage} className="p-3 border-t border-gray-200 bg-white flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Query metrics, AI experience, scale..."
                  disabled={isTyping}
                  className="flex-1 border border-gray-300 p-2 text-sm focus:outline-none focus:border-black transition-colors disabled:bg-gray-100"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isTyping}
                  className="bg-black text-white p-2 px-4 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          )}

          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            className="h-14 w-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 bg-black text-white hover:scale-105 hover:bg-indigo-600"
            style={{ transformOrigin: 'center' }}
          >
            <MessageSquare size={24} />
          </button>
        </div>
      </div>
    </div>
  );
}
